/**
 * Conversor do XLSX de compensação da CEMIG (arquivo FCB gerado pelo Rebalance)
 * para o novo layout CSV exigido pela concessionária.
 *
 * Entrada  : planilha FCB — dados a partir da linha 16, colunas A (UC),
 *            B (CPF/CNPJ) e C (Cota, em pontos percentuais).
 * Saída    : CSV com separador ";", sem cabeçalho, quatro campos por linha:
 *            UC;CPF/CNPJ;Cota;Âncora
 *
 * Regra nova de negócio: a UC de maior injeção é sempre a âncora. Como dentro
 * de um mesmo arquivo (uma usina, um ciclo) a injeção é proporcional à cota,
 * a âncora é a linha de maior cota — empate resolve pela primeira ocorrência.
 *
 * Todo o processamento acontece no navegador: nada é enviado para servidor.
 */

import { lerPrimeiraPlanilha } from "./xlsxReader";

export const LINHA_INICIAL_DADOS = 16;
export const CABECALHO_CSV = "UC;CPF/CNPJ;Cota;Âncora";

// A CEMIG rejeita cota zero em beneficiária — mesma regra já aplicada no backend.
const COTA_MINIMA = 0.0001;

// Acima disso a planilha está em pontos percentuais; até esse limite está no
// formato percentual do Excel (0,2005 = 20,05%) e precisa ser multiplicada por 100.
const LIMITE_ESCALA_PERCENTUAL = 1.05;

const MAX_LINHAS_MENSAGEM = 10;

export interface LinhaCemig {
    numeroLinha: number;
    uc: string;
    documento: string;
    cota: number;
    cotaTexto: string;
    ancora: boolean;
}

export interface ResultadoConversao {
    nomeArquivo: string;
    nomeCsv: string;
    linhas: LinhaCemig[];
    somaCotas: number;
    ancora: LinhaCemig | null;
    erros: string[];
    avisos: string[];
}

interface LinhaBruta {
    numeroLinha: number;
    uc: string;
    documento: string;
    cota: number;
}

/** Cota com vírgula decimal, de 2 a 4 casas, sem "%" e sem separador de milhar. */
export function formatarCota(valor: number): string {
    let texto = (Math.round(valor * 10000) / 10000).toFixed(4);

    while (texto.slice(-1) === "0" && texto.split(".")[1].length > 2) {
        texto = texto.slice(0, -1);
    }

    return texto.replace(".", ",");
}

function apenasDigitos(texto: string): string {
    return texto.replace(/\D/g, "");
}

/** Converte notação científica / "123.0" de volta para o inteiro original. */
function textoInteiro(bruto: string): string {
    if (/^\d+(\.\d+)?[eE][+-]?\d+$/.test(bruto)) {
        const numero = Number(bruto);

        if (isFinite(numero)) {
            return numero.toFixed(0);
        }
    }

    if (/^\d+\.0+$/.test(bruto)) {
        return bruto.split(".")[0];
    }

    return bruto;
}

function normalizarUc(bruto: string): string {
    return textoInteiro(bruto.trim());
}

function normalizarDocumento(bruto: string): string {
    const texto = textoInteiro(bruto.trim());

    // Célula numérica perde os zeros à esquerda do CPF/CNPJ — recompõe pelo tamanho.
    if (/^\d+$/.test(texto)) {
        if (texto.length <= 11) {
            return texto.padStart(11, "0");
        }

        if (texto.length <= 14) {
            return texto.padStart(14, "0");
        }
    }

    return texto;
}

function parsearNumero(bruto: string): number {
    let texto = bruto.trim().replace(/%/g, "").replace(/\s/g, "");

    if (!texto) {
        return NaN;
    }

    if (texto.indexOf(",") >= 0) {
        texto = texto.replace(/\./g, "").replace(",", ".");
    }

    const numero = Number(texto);

    return isNaN(numero) ? NaN : numero;
}

function listarLinhas(numeros: number[]): string {
    const visiveis = numeros.slice(0, MAX_LINHAS_MENSAGEM).join(", ");

    return numeros.length > MAX_LINHAS_MENSAGEM
        ? `${visiveis} e mais ${numeros.length - MAX_LINHAS_MENSAGEM}`
        : visiveis;
}

function temNaoAscii(texto: string): boolean {
    // eslint-disable-next-line no-control-regex
    return /[^\x00-\x7F]/.test(texto);
}

/** Lê o XLSX do Rebalance e devolve as linhas já no formato de saída da CEMIG. */
export async function converterArquivoCemig(arquivo: File): Promise<ResultadoConversao> {
    const erros: string[] = [];
    const avisos: string[] = [];
    const nomeCsv = `${arquivo.name.replace(/\.[^.]+$/, "")}.csv`;

    const vazio: ResultadoConversao = {
        nomeArquivo: arquivo.name,
        nomeCsv,
        linhas: [],
        somaCotas: 0,
        ancora: null,
        erros,
        avisos,
    };

    const linhasPlanilha = await lerPrimeiraPlanilha(arquivo);
    const porNumero: { [numero: number]: { [coluna: string]: string } } = {};
    let ultimaLinha = 0;

    linhasPlanilha.forEach((linha) => {
        porNumero[linha.numero] = linha.celulas;

        if (linha.numero > ultimaLinha) {
            ultimaLinha = linha.numero;
        }
    });

    const brutas: LinhaBruta[] = [];

    for (let numero = LINHA_INICIAL_DADOS; numero <= ultimaLinha; numero++) {
        const celulas = porNumero[numero] || {};
        const uc = normalizarUc(celulas["A"] || "");
        const documento = normalizarDocumento(celulas["B"] || "");
        const cotaBruta = (celulas["C"] || "").trim();

        // Primeira linha com A, B e C vazias encerra os dados.
        if (!uc && !documento && !cotaBruta) {
            break;
        }

        brutas.push({ numeroLinha: numero, uc, documento, cota: parsearNumero(cotaBruta) });
    }

    if (brutas.length === 0) {
        erros.push(`Nenhuma linha de dados encontrada a partir da linha ${LINHA_INICIAL_DADOS} da planilha.`);
        return vazio;
    }

    const somaBruta = brutas.reduce((total, item) => total + (isNaN(item.cota) ? 0 : item.cota), 0);
    const escalaPercentual = somaBruta > 0 && somaBruta <= LIMITE_ESCALA_PERCENTUAL;

    if (escalaPercentual) {
        avisos.push(
            "Planilha no formato percentual do Excel (0,2005 = 20,05%). As cotas foram multiplicadas por 100."
        );
    }

    const semUc: number[] = [];
    const semDocumento: number[] = [];
    const cotaInvalida: number[] = [];
    const documentoInvalido: number[] = [];
    const cotaZerada: number[] = [];
    const naoAscii: number[] = [];
    const ocorrenciasUc: { [uc: string]: number } = {};

    const linhas: LinhaCemig[] = brutas.map((item) => {
        const cotaEscalada = isNaN(item.cota) ? NaN : escalaPercentual ? item.cota * 100 : item.cota;
        const cota = isNaN(cotaEscalada) ? 0 : cotaEscalada === 0 ? COTA_MINIMA : cotaEscalada;

        if (!item.uc) {
            semUc.push(item.numeroLinha);
        }

        if (!item.documento) {
            semDocumento.push(item.numeroLinha);
        }

        if (isNaN(item.cota)) {
            cotaInvalida.push(item.numeroLinha);
        } else if (cotaEscalada === 0) {
            cotaZerada.push(item.numeroLinha);
        }

        const digitos = apenasDigitos(item.documento);

        if (item.documento && digitos.length !== 11 && digitos.length !== 14) {
            documentoInvalido.push(item.numeroLinha);
        }

        if (item.uc) {
            ocorrenciasUc[item.uc] = (ocorrenciasUc[item.uc] || 0) + 1;
        }

        const cotaTexto = formatarCota(cota);

        if (temNaoAscii(item.uc) || temNaoAscii(item.documento)) {
            naoAscii.push(item.numeroLinha);
        }

        return {
            numeroLinha: item.numeroLinha,
            uc: item.uc,
            documento: item.documento,
            cota,
            cotaTexto,
            ancora: false,
        };
    });

    // Âncora: maior cota do arquivo; empate fica com a primeira ocorrência.
    let indiceAncora = 0;

    linhas.forEach((linha, indice) => {
        if (linha.cota > linhas[indiceAncora].cota) {
            indiceAncora = indice;
        }
    });

    linhas[indiceAncora].ancora = true;

    // Soma pelas cotas informadas na planilha: o piso de 0,0001 aplicado às cotas
    // zeradas não pode empurrar o arquivo artificialmente acima dos 100%.
    const somaCotas = escalaPercentual ? somaBruta * 100 : somaBruta;

    if (semUc.length > 0) {
        erros.push(`UC não preenchida na(s) linha(s): ${listarLinhas(semUc)}.`);
    }

    if (semDocumento.length > 0) {
        erros.push(`CPF/CNPJ não preenchido na(s) linha(s): ${listarLinhas(semDocumento)}.`);
    }

    if (cotaInvalida.length > 0) {
        erros.push(`Cota vazia ou inválida na(s) linha(s): ${listarLinhas(cotaInvalida)}.`);
    }

    if (documentoInvalido.length > 0) {
        erros.push(
            `CPF/CNPJ com quantidade de dígitos diferente de 11 ou 14 na(s) linha(s): ${listarLinhas(
                documentoInvalido
            )}.`
        );
    }

    if (somaCotas > 100 + 1e-9) {
        erros.push(`A soma das cotas é ${formatarCota(somaCotas)}%, acima do limite de 100% aceito pela CEMIG.`);
    } else if (somaCotas < 100 - 1e-9) {
        avisos.push(
            `A soma das cotas é ${formatarCota(somaCotas)}%. O percentual restante fica com a UC âncora.`
        );
    }

    if (cotaZerada.length > 0) {
        avisos.push(`Cota zero convertida para 0,0001 na(s) linha(s): ${listarLinhas(cotaZerada)}.`);
    }

    const ucsRepetidas = Object.keys(ocorrenciasUc).filter((uc) => ocorrenciasUc[uc] > 1);

    if (ucsRepetidas.length > 0) {
        avisos.push(
            `UC repetida no arquivo (a CEMIG soma as cotas das linhas repetidas): ${ucsRepetidas
                .slice(0, MAX_LINHAS_MENSAGEM)
                .join(", ")}.`
        );
    }

    if (naoAscii.length > 0) {
        avisos.push(`Caractere especial encontrado na(s) linha(s): ${listarLinhas(naoAscii)}.`);
    }

    return {
        nomeArquivo: arquivo.name,
        nomeCsv,
        linhas,
        somaCotas,
        ancora: linhas[indiceAncora],
        erros,
        avisos,
    };
}

/** Monta o CSV final: separador ";", quebra \r\n e sem linha vazia no fim. */
export function gerarCsv(resultado: ResultadoConversao, incluirCabecalho: boolean): string {
    const linhas = resultado.linhas.map(
        (linha) => `${linha.uc};${linha.documento};${linha.cotaTexto};${linha.ancora ? "Sim" : ""}`
    );

    if (incluirCabecalho) {
        linhas.unshift(CABECALHO_CSV);
    }

    return linhas.join("\r\n");
}

/** Baixa o conteúdo como .csv em UTF-8 sem BOM. */
export function baixarCsv(nomeArquivo: string, conteudo: string) {
    const url = URL.createObjectURL(new Blob([conteudo], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");

    link.href = url;
    link.download = nomeArquivo;
    link.click();

    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
